<!--
  Public App.svelte — Root shell for the public (auth) application.

  Renders a full-screen layout for all auth pages (login, register,
  forgot-password, etc.).  No sidebar, no navbar — just the Router.

  If the user is already authenticated and lands on a public route,
  they are redirected to the admin app at `/Manage/`.
  If the URL is exactly `/` (root), they are redirected to `/login`
  (unauthenticated) or `/Manage/` (authenticated).
-->
<script lang="ts">
	import '../app.css';
	import { SvelteToast } from '@zerodevx/svelte-toast';
	import { Alert, Heading, P } from 'flowbite-svelte';

	import Router, { location, querystring } from '../abstract-spa-router';

	import { isManagementAppPath } from '../authRedirect';

	import routes from './routes';
	import site from '../site';

	import { isConfigurationLoaded, configurationError } from '../AppConfiguration';
	import { isAuthorizationPending, isAuthorized } from '../Auth';
	import { t } from '../i18n';

	/**
	 * Redirect authenticated users from public routes to the admin app.
	 * The root `/` case is already handled in main.ts before mount.
	 *
	 * Exceptions:
	 * - `previewConsent=true` in query string: admin previewing consent page
	 * - `/authorize` path: user needs to consent to an application
	 * - `/logout` path: signing out necessarily starts while still authenticated
	 */
	$effect.pre(() => {
		const isOnAuthorizePage = $location === '/authorize';
		/* Sign-out is reached with a valid session by definition, so the guard
		   must let it through or it would bounce the user back into the app. */
		const isOnLogoutPage = $location === '/logout';
		const qs = new URLSearchParams($querystring);
		const isPreview = qs.has('previewConsent');
		/* When Login/TwoFactor are mid-flight bouncing the user to /authorize
		   after setToken(), skip the guard so it doesn't race with that SPA
		   push and hijack the user to /Manage/. Two URL shapes carry that
		   in-flight context: the legacy `redirect=<encoded /authorize?…>`
		   used by invitation emails, and the top-level params shape
		   (`app=…&returnURL=…` or with `oauth=true&client_id=…`) used by
		   the Authorize.svelte → Login → TwoFactor handoff. */
		const hasAuthFlowParam = qs.has('redirect') || qs.has('app') || qs.has('client_id');

		/* Already signed in and sent to sign in on the way back to a management
		   page (the storefront does this): there is nothing to sign in to, so
		   go straight there rather than showing a login form to a signed-in user. */
		const redirect = qs.get('redirect') ?? '';
		/* Already signed in and handed over from the storefront to a management page: skip the form. */
		if (!$isAuthorizationPending && $isAuthorized && isManagementAppPath(redirect)) {
			window.location.href = redirect;
			return;
		}

		/** Redirect authenticated users to dashboard unless they are on the authorize
		 *  page, previewing consent, or mid-login with a pending redirect to /authorize. */
		if (
			!$isAuthorizationPending &&
			$isAuthorized &&
			!isPreview &&
			!isOnAuthorizePage &&
			!isOnLogoutPage &&
			!hasAuthFlowParam
		) {
			console.log('[PublicApp] guard firing → /Manage/', { location: $location, querystring: $querystring });
			window.location.href = '/Manage/';
		}
	});
</script>

{#if $isConfigurationLoaded}
	<Router {routes} restoreScrollState={true} />
{:else if ($configurationError ?? '').length > 0}
	<div class="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
		<Alert>
			<Heading tag="h1" class="text-2xl font-bold">
				<i class="fa-solid fa-bug mr-2 inline pb-3 text-5xl"></i>{$t("No Backend Response")}
			</Heading>
			<P>{$t("{{name}} is currently unavailable. Please try again later.", { name: site.name })}</P>
		</Alert>
	</div>
{/if}

<SvelteToast />
